import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { trigger, transition, style, animate } from '@angular/animations';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from '@modules/auth/services/auth.service';
import { PermissionsService } from '@shared/services/permissions.service';
import { UserRole } from '@modules/auth/models/enums/user-role.enum';
import { REQUEST_BOARD_MODERATE_PERMISSION } from '@modules/request-board/guards/request-board-moderate.guard';

// UI menu model (supports nesting)
interface SidebarMenuItem {
  id?: number | string;
  label: string;
  emoji?: string;
  icon?: string;
  route?: string;
  queryParams?: { [key: string]: string };
  command?: () => void;
  active?: boolean;
  expanded?: boolean;
  children?: SidebarMenuItem[];
  /** Kod funkcji systemowej wymaganej, by pozycja była widoczna (SUPER_ADMIN widzi zawsze). */
  systemFunction?: string;
}

// Backend payload shapes
interface ProjectNode {
  id: number;
  name: string;
  slug?: string;
  children?: { id: number; name: string; route?: string; slug?: string; }[];
}

interface AcademyNode {
  id: number;
  name: string;
  slug?: string;
  projects: ProjectNode[];
}

interface MenuPayload {
  academies: AcademyNode[];
}

@Component({
  standalone: true,
  selector: 'app-dashboard-sidebar',
  templateUrl: './dashboard-sidebar.component.html',
  styleUrl: './dashboard-sidebar.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    RippleModule,
    AvatarModule,
    TooltipModule,
    TranslocoModule
  ],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('150ms ease-in', style({ height: 0, opacity: 0 }))
      ])
    ])
  ]
})
export class DashboardSidebarComponent implements OnInit {
  @Input() isMobile: boolean = false;
  @Output() menuItemClick = new EventEmitter<void>();

  private authService = inject(AuthService);
  private permissionsService = inject(PermissionsService);
  private destroyRef = inject(DestroyRef);
  private transloco = inject(TranslocoService);

  userMenuExpanded = false;

  menuItems: SidebarMenuItem[] = [
    {
      label: 'layout.sidebar.menu.dashboard',
      icon: 'pi pi-home',
      route: '/dashboard/overview',
      active: true
    },
  ];

  userInfo = {
    name: 'Jan Kowalski',
    status: 'Premium',
    avatar: 'JK'
  };

  constructor(private router: Router) {
    this.buildMenuFromPayload();
  }

  ngOnInit(): void {
    // Ustaw aktywny element na podstawie obecnego URL
    this.updateActiveStateFromRoute(this.router.url);

    // Pozycje gated uprawnieniami (np. „Szukam noclegu”) — przebuduj menu, gdy zmieni się
    // użytkownik lub jego uprawnienia (login, logout, odświeżenie uprawnień z GET /auth/permissions
    // na starcie aplikacji i w guardzie modułu — AuthService.refreshPermissions).
    combineLatest([this.authService.currentUser$, this.permissionsService.userPermissions$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildMenuFromPayload();
        this.updateActiveStateFromRoute(this.router.url);
      });

    // Nasłuchuj zmian routingu
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateActiveStateFromRoute(event.urlAfterRedirects);
      });
  }

  onMenuItemClick(item: SidebarMenuItem): void {
    if (item.route) {
      this.router.navigate([item.route], {
        queryParams: item.queryParams
      });

      // Mark as active
      const markInactive = (items: SidebarMenuItem[]) => {
        items.forEach(mi => {
          mi.active = false;
          if (mi.children?.length) markInactive(mi.children);
        });
      };
      markInactive(this.menuItems);
      item.active = true;
    }

    if (item.command) {
      item.command();
    }

    if (this.isMobile) {
      this.menuItemClick.emit();
    }
  }

  toggleCategory(item: SidebarMenuItem): void {
    item.expanded = !item.expanded;
  }

  toggleExpand(item: SidebarMenuItem, event: Event): void {
    event.stopPropagation();
    item.expanded = !item.expanded;
  }

  onNewAcademy(): void {
    // Tutaj możesz dodać logikę otwarcia modala lub nawigacji do formularza tworzenia akademii
    console.log('Tworzenie nowej akademii...');
    // Przykład: this.router.navigate(['/academy/new']);
    // Lub: otwórz modal, emituj event, itp.
  }

  toggleUserMenu(): void {
    this.userMenuExpanded = !this.userMenuExpanded;
  }

  onUserMenuAction(action: 'profile' | 'settings' | 'logout'): void {
    this.userMenuExpanded = false;
    
    switch (action) {
      case 'profile':
        this.router.navigate(['/profile']);
        break;
      case 'settings':
        this.router.navigate(['/settings']);
        break;
      case 'logout':
        this.authService.logout();
        break;
    }

    if (this.isMobile) {
      this.menuItemClick.emit();
    }
  }

  buildMenuFromPayload(_payload?: MenuPayload): void {
    this.menuItems = [
      {
        label: 'layout.sidebar.menu.dashboard',
        emoji: '📊',
        route: '/dashboard/overview',
        active: true
      },
      {
        label: 'layout.sidebar.menu.users',
        icon: 'pi pi-users',
        route: '/user'
      },
      {
        label: 'layout.sidebar.menu.legalDocuments',
        icon: 'pi pi-file-edit',
        expanded: false,
        children: [
          {
            label: 'layout.sidebar.menu.termsOfService',
            icon: 'pi pi-file',
            route: '/legal/documents',
            queryParams: { type: 'terms_of_service' }
          },
          {
            label: 'layout.sidebar.menu.privacyPolicy',
            icon: 'pi pi-shield',
            route: '/legal/documents',
            queryParams: { type: 'privacy_policy' }
          },
          {
            label: 'layout.sidebar.menu.cookiesPolicy',
            icon: 'pi pi-database',
            route: '/legal/documents',
            queryParams: { type: 'cookie_policy' }
          },
          {
            label: 'layout.sidebar.menu.dataProcessingInfo',
            icon: 'pi pi-info-circle',
            route: '/legal/documents',
            queryParams: { type: 'data_processing_info' }
          },
          // {
          //   label: 'layout.sidebar.menu.otherDocuments',
          //   icon: 'pi pi-folder',
          //   route: '/legal/documents',
          //   queryParams: { type: 'other' }
          // }
        ]
      },
      {
        label: 'layout.sidebar.menu.amenities',
        icon: 'pi pi-star',
        route: '/amenities'
      },
      {
        label: 'layout.sidebar.menu.reports',
        icon: 'pi pi-flag',
        expanded: false,
        children: [
          {
            label: 'layout.sidebar.menu.reportsList',
            icon: 'pi pi-list',
            route: '/reports'
          },
          {
            label: 'layout.sidebar.menu.reportsCases',
            icon: 'pi pi-briefcase',
            route: '/reports/cases'
          },
          {
            label: 'layout.sidebar.menu.reportsStats',
            icon: 'pi pi-chart-bar',
            route: '/reports/stats'
          }
        ]
      },
      {
        label: 'layout.sidebar.menu.requestBoard',
        icon: 'pi pi-search',
        expanded: false,
        systemFunction: REQUEST_BOARD_MODERATE_PERMISSION,
        // Kolejność ma znaczenie dla podświetlania: '/request-board' pasuje też do
        // '/request-board/reports' i '?needsModeration=true', więc lista jest ostatnia.
        children: [
          {
            label: 'layout.sidebar.menu.requestBoardModeration',
            icon: 'pi pi-exclamation-triangle',
            route: '/request-board',
            queryParams: { needsModeration: 'true' }
          },
          {
            label: 'layout.sidebar.menu.requestBoardReports',
            icon: 'pi pi-flag',
            route: '/request-board/reports'
          },
          {
            label: 'layout.sidebar.menu.requestBoardBlockedOrgs',
            icon: 'pi pi-ban',
            route: '/request-board/blocked-orgs'
          },
          {
            label: 'layout.sidebar.menu.requestBoardList',
            icon: 'pi pi-list',
            route: '/request-board'
          }
        ]
      },
      {
        label: 'layout.sidebar.menu.consents',
        icon: 'pi pi-check-square',
        expanded: false,
        children: [
          {
            label: 'layout.sidebar.menu.consentTypes',
            icon: 'pi pi-list',
            route: '/legal/consents/types'
          },
          // {
          //   label: 'layout.sidebar.menu.consentStats',
          //   icon: 'pi pi-chart-bar',
          //   route: '/legal/consents/stats'
          // }
        ]
      },
      {
        label: 'layout.sidebar.menu.propertyRanking',
        icon: 'pi pi-chart-line',
        route: '/property-ranking'
      },
      {
        label: 'layout.sidebar.menu.emailSender',
        icon: 'pi pi-send',
        route: '/email-sender'
      },
    ].filter(item => this.isMenuItemVisible(item));
  }

  /** Pozycja z `systemFunction` widoczna tylko z uprawnieniem (SUPER_ADMIN zawsze). */
  private isMenuItemVisible(item: SidebarMenuItem): boolean {
    if (!item.systemFunction) {
      return true;
    }
    if (this.authService.currentUser$.value?.role === UserRole.SUPER_ADMIN) {
      return true;
    }
    return this.permissionsService.hasSystemFunction(item.systemFunction);
  }

  private updateActiveStateFromRoute(url: string): void {
    // Parse URL to separate path and query params
    const [urlPath, urlQueryString] = url.split('?');
    const urlParams = new URLSearchParams(urlQueryString || '');

    // Wyczyść wszystkie aktywne stany
    const clearActiveState = (items: SidebarMenuItem[]) => {
      items.forEach(item => {
        item.active = false;
        if (item.children?.length) {
          clearActiveState(item.children);
        }
      });
    };
    clearActiveState(this.menuItems);

    // Znajdź i zaznacz aktywny element + rozwiń wszystkie nadrzędne
    const findAndActivate = (items: SidebarMenuItem[], parents: SidebarMenuItem[] = []): boolean => {
      for (const item of items) {
        // Najpierw sprawdź czy któreś dziecko pasuje dokładnie
        if (item.children?.length) {
          const childFound = findAndActivate(item.children, [...parents, item]);
          if (childFound) {
            return true;
          }
        }

        // Check if route matches
        const routeMatches = item.route && (
          urlPath === item.route ||
          urlPath.startsWith(item.route + '/')
        );

        // Check if queryParams match (if item has queryParams)
        let queryParamsMatch = true;
        if (item.queryParams) {
          queryParamsMatch = Object.entries(item.queryParams).every(
            ([key, value]) => urlParams.get(key) === value
          );
        }

        const isMatch = routeMatches && queryParamsMatch;

        if (isMatch) {
          // Zaznacz element jako aktywny
          item.active = true;

          // Rozwiń wszystkie elementy nadrzędne
          parents.forEach(parent => parent.expanded = true);

          // Jeśli ma dzieci, rozwiń również ten element
          if (item.children?.length) {
            item.expanded = true;
          }

          return true;
        }
      }
      return false;
    };

    findAndActivate(this.menuItems);
  }
}
